/*
   Data shape for the review queue.

   1. UI rows (`QueueRow`) are what the table wants to render.
   2. Wire types (`ThreadOutWire`) mirror the backend response of
      `GET /api/v1/threads` (routes_workflow.py:42–61). `toQueueRow`
      is the adapter — the one boundary where numeric backend IDs become
      branded strings and where thread → per-current-version projection
      happens.
   3. Pure helpers (`filterQueue`, `sortQueue`, `paginate`, `tally`) stay
      here so the fetch hook can compose them without React.
*/

/** Branded id so a raw string can't be mistaken for a ProductId at a boundary. */
export type ProductId = string & { readonly __brand: "ProductId" }

export type QueueStatus = "queued" | "in_review" | "needs_sme" | "cleared"

export interface QueueRow {
  id: ProductId
  product: string
  epaRegNo: string
  status: QueueStatus
  versions: number
  findingsOutstanding: number
  findingsCorrected: number
  commsUnread: number
  owner: string
  submittedBy: string
  updatedAt: string
}

export type SortField = "updatedAt" | "product" | "findings" | "versions"
export type SortDir = "asc" | "desc"
export interface QuerySort {
  field: SortField
  dir: SortDir
}

export interface QueueQuery extends QueueFilters {
  page: number
  pageSize: number
  search?: string
  sort?: QuerySort
}

export interface QueueResult {
  rows: QueueRow[]
  total: number
  counts: Record<QueueStatus, number>
}

/* ------- presentation config (data-adjacent, view-agnostic) ------- */

export const STATUS_LABEL: Record<QueueStatus, string> = {
  queued: "Queued",
  in_review: "In review",
  needs_sme: "Needs SME",
  cleared: "Cleared",
}

export const STATUS_TONE: Record<QueueStatus, "info" | "print" | "people" | "master"> = {
  queued: "info",
  in_review: "print",
  needs_sme: "people",
  cleared: "master",
}

/* ---------------------------- wire types --------------------------- */

/** Per-version entry inside a ThreadOut. Matches routes_workflow.py:42. */
interface VersionEntryWire {
  review_id: number
  version_no: number
  status: string
  stage: string | null
  outstanding: number
  corrected: number
  unread: number
  archived: boolean
}

/** GET /api/v1/threads returns list<ThreadOut>. Exported so `queue-api.ts`
 * can type the request result without re-declaring the wire. */
export interface ThreadOutWire {
  thread_id: string
  reg_nr: string
  product_name: string | null
  client_id: number | null
  current_review_id: number
  version_count: number
  versions: VersionEntryWire[]
}

/**
 * Map a backend review status onto the queue's four-bucket status.
 * Backend: pending | running | completed | failed | needs_sme | verified.
 * The queue rolls these into visual buckets:
 *   pending/running       → "queued"
 *   completed with open   → "in_review"
 *   needs_sme             → "needs_sme"
 *   verified              → "cleared"
 *   completed no open     → "cleared" (nothing left to do)
 *   failed                → "in_review" (needs attention, borrow the tone)
 */
function toQueueStatus(v: VersionEntryWire): QueueStatus {
  if (v.status === "needs_sme") return "needs_sme"
  if (v.status === "verified") return "cleared"
  if (v.status === "pending" || v.status === "running") return "queued"
  if (v.status === "completed") return v.outstanding > 0 ? "in_review" : "cleared"
  // failed → surface it so it doesn't hide as "cleared"
  return "in_review"
}

/**
 * Enrichment maps built from side calls so we can fill columns the /threads
 * response does not carry: assignee full name, client name, and an updated-at
 * timestamp. Real fix is a backend PR (denormalize onto ThreadOut); this is
 * the "make it visible now" bridge.
 */
export interface QueueEnrichment {
  /** review_id → {assignee_id, client_id, most-recent activity timestamp} */
  reviewMeta: Map<number, { assigneeId: number | null; clientId: number | null; updatedAt: string }>
  /** user_id → display name; empty when the caller is a client (no /users access) */
  userNames: Map<number, string>
  /** client_id → engagement name; empty when the caller is not admin (no /clients access) */
  clientNames: Map<number, string>
}

const EMPTY_ENRICHMENT: QueueEnrichment = {
  reviewMeta: new Map(),
  userNames: new Map(),
  clientNames: new Map(),
}

/**
 * ThreadOut → QueueRow. The current-version entry drives status + tallies;
 * older versions contribute their outstanding/corrected totals so the row
 * counts reflect the whole correction loop, not just the latest attempt.
 *
 * When enrichment maps are provided, owner / submittedBy / updatedAt come
 * from the joined data. Missing entries fall back to "—" for names or the
 * current time for the timestamp (so sorts remain sane).
 */
export function toQueueRow(t: ThreadOutWire, enr: QueueEnrichment = EMPTY_ENRICHMENT): QueueRow {
  const current =
    t.versions.find((v) => v.review_id === t.current_review_id) ?? t.versions[0]
  const outstanding = t.versions.reduce((a, v) => a + v.outstanding, 0)
  const corrected = t.versions.reduce((a, v) => a + v.corrected, 0)
  const unread = t.versions.reduce((a, v) => a + v.unread, 0)
  const meta = enr.reviewMeta.get(t.current_review_id)
  const ownerName =
    meta?.assigneeId != null ? (enr.userNames.get(meta.assigneeId) ?? `User #${meta.assigneeId}`) : "—"
  const clientId = meta?.clientId ?? t.client_id
  const clientName =
    clientId != null ? (enr.clientNames.get(clientId) ?? `Client #${clientId}`) : "—"
  return {
    id: String(t.current_review_id) as ProductId,
    product: t.product_name ?? t.reg_nr,
    epaRegNo: t.reg_nr,
    status: toQueueStatus(current),
    versions: t.version_count,
    findingsOutstanding: outstanding,
    findingsCorrected: corrected,
    commsUnread: unread,
    owner: ownerName,
    submittedBy: clientName,
    updatedAt: meta?.updatedAt ?? new Date().toISOString(),
  }
}


/* -------------------------- pure helpers -------------------------- */

const EMPTY_COUNTS: Record<QueueStatus, number> = {
  queued: 0,
  in_review: 0,
  needs_sme: 0,
  cleared: 0,
}

export function tally(rows: readonly QueueRow[]): Record<QueueStatus, number> {
  const out = { ...EMPTY_COUNTS }
  for (const r of rows) out[r.status]++
  return out
}

/** Filter fields that queryQueue understands. Mirrors the API contract. */
export interface QueueFilters {
  status?: QueueStatus | null
  assignee?: string[]
  submitter?: string[]
  findingsState?: "outstanding" | "corrected" | "none"
  hasUnread?: boolean
  updatedAt?: { from: string | null; to: string | null }
}

export function filterQueue(
  rows: readonly QueueRow[],
  opts: QueueFilters & { search?: string },
): QueueRow[] {
  const needle = opts.search?.trim().toLowerCase() ?? ""
  const assignees = opts.assignee && opts.assignee.length > 0 ? new Set(opts.assignee) : null
  const submitters = opts.submitter && opts.submitter.length > 0 ? new Set(opts.submitter) : null
  const fromMs = opts.updatedAt?.from ? Date.parse(opts.updatedAt.from) : null
  const toMs = opts.updatedAt?.to ? Date.parse(opts.updatedAt.to) : null

  return rows.filter((r) => {
    if (opts.status && r.status !== opts.status) return false
    if (assignees && !assignees.has(r.owner)) return false
    if (submitters && !submitters.has(r.submittedBy)) return false
    if (opts.findingsState === "outstanding" && r.findingsOutstanding === 0) return false
    if (
      opts.findingsState === "corrected" &&
      !(r.findingsOutstanding === 0 && r.findingsCorrected > 0)
    ) {
      return false
    }
    if (
      opts.findingsState === "none" &&
      !(r.findingsOutstanding === 0 && r.findingsCorrected === 0)
    ) {
      return false
    }
    if (opts.hasUnread === true && r.commsUnread === 0) return false
    if (fromMs != null || toMs != null) {
      const rowMs = Date.parse(r.updatedAt)
      if (fromMs != null && rowMs < fromMs) return false
      if (toMs != null && rowMs > toMs + 24 * 60 * 60 * 1000 - 1) return false // inclusive end-of-day
    }
    if (!needle) return true
    return (
      r.product.toLowerCase().includes(needle) ||
      r.epaRegNo.toLowerCase().includes(needle) ||
      r.submittedBy.toLowerCase().includes(needle)
    )
  })
}

export function sortQueue(rows: readonly QueueRow[], sort?: QuerySort): QueueRow[] {
  if (!sort) return [...rows]
  const dir = sort.dir === "asc" ? 1 : -1
  const cmp = SORTERS[sort.field]
  return [...rows].sort((a, b) => cmp(a, b) * dir)
}

const SORTERS: Record<SortField, (a: QueueRow, b: QueueRow) => number> = {
  updatedAt: (a, b) => a.updatedAt.localeCompare(b.updatedAt),
  product: (a, b) => a.product.localeCompare(b.product),
  versions: (a, b) => a.versions - b.versions,
  findings: (a, b) => a.findingsOutstanding - b.findingsOutstanding,
}

export function paginate<T>(rows: readonly T[], page: number, pageSize: number): T[] {
  const start = Math.max(0, (page - 1) * pageSize)
  return rows.slice(start, start + pageSize)
}

/**
 * The one entry point a real react-query hook will map to a single
 * request. Kept synchronous today; the network cost lives in the hook.
 */
export function queryQueue(source: readonly QueueRow[], q: QueueQuery): QueueResult {
  const filtered = filterQueue(source, {
    status: q.status,
    assignee: q.assignee,
    submitter: q.submitter,
    findingsState: q.findingsState,
    hasUnread: q.hasUnread,
    updatedAt: q.updatedAt,
    search: q.search,
  })
  const sorted = sortQueue(filtered, q.sort)
  return {
    rows: paginate(sorted, q.page, q.pageSize),
    total: filtered.length,
    counts: tally(source),
  }
}

/** Option lists derived from the dataset — real API returns these as endpoints. */
export function assigneeOptions(
  rows: readonly QueueRow[],
): Array<{ value: string; label: string }> {
  const seen = new Set<string>()
  for (const r of rows) if (r.owner && r.owner !== "—") seen.add(r.owner)
  return Array.from(seen)
    .sort((a, b) => a.localeCompare(b))
    .map((v) => ({ value: v, label: v }))
}

export function submitterOptions(
  rows: readonly QueueRow[],
): Array<{ value: string; label: string }> {
  const seen = new Set<string>()
  for (const r of rows) if (r.submittedBy) seen.add(r.submittedBy)
  return Array.from(seen)
    .sort((a, b) => a.localeCompare(b))
    .map((v) => ({ value: v, label: v }))
}

/*
   Data-fetch entry point moved to `./queue-api.ts` (React Query hook
   `useThreadsQuery`). This file stays focused on wire types, adapters, and
   the pure query helpers (filter/sort/paginate/tally). Keeps the API surface
   testable in isolation from the transport.
*/
